import React, { useEffect } from 'react'
import RippleBackground from './RippleBackground'
import { RotateCcw, Users, Check, ArrowDown, Calendar } from 'lucide-react'
import { Role } from '../types'

interface EndCallScreenProps {
  role: Role
  onRestart: () => void
  onChangeRole: () => void
  onKeepScrolling: () => void
  isWebsiteMode?: boolean
}

const EndCallScreen: React.FC<EndCallScreenProps> = ({
  role,
  onRestart,
  onChangeRole,
  onKeepScrolling,
  isWebsiteMode = false
}) => {
  // Stop any playing audio when EndCallScreen mounts
  useEffect(() => {
    console.log('EndCallScreen: Stopping any playing audio')
    
    // Stop all audio elements
    const audioElements = document.querySelectorAll('audio')
    audioElements.forEach(audio => {
      if (audio && !audio.paused) {
        console.log('EndCallScreen: Stopping audio element')
        audio.pause()
        audio.currentTime = 0
      }
    })
    
    // Try to stop any Web Audio API contexts
    // Note: We can't directly access contexts from other components
    // but we can try to stop any active audio
  }, [])

  const handleScheduleDemo = () => {
    window.open('https://getgeek.ai/free-trial-of-ai-recruiting-tool', '_blank')
  }
  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-between relative overflow-hidden p-4 sm:p-8 md:p-16">
      {/* Background Video */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="https://pub-b4a0d7160e804448b1c1062c499df6e5.r2.dev/background.mp4" type="video/mp4" />
      </video>
      
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      {/* Background concentric circles - shared */}
      <RippleBackground anchorToParentCenter />

      {/* Top section - Role title and Action buttons */}
      <div className="relative z-10 flex flex-col items-center space-y-4 sm:space-y-6 md:space-y-8">
        {/* Role title */}
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white text-center">{role.title}</h1>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Restart button */}
          <button
            onClick={onRestart}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-white text-white rounded-full hover:bg-white hover:text-purple-600 transition-all duration-200 w-full sm:w-auto justify-center"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-sm font-medium">Restart</span>
          </button>

          {/* Change Role button */}
          <button
            onClick={onChangeRole}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-white text-white rounded-full hover:bg-white hover:text-purple-600 transition-all duration-200 w-full sm:w-auto justify-center"
          >
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Change Role</span>
          </button>
        </div>
      </div>

      {/* Central logo - same as calling screen */}
      <div className="relative z-10 flex items-center justify-center">
        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center shadow-2xl">
          <img 
            src="https://public-getgeek-s3-bucket-storage.s3.amazonaws.com/qrs/zayn/center_call_interface.png" 
            alt="Zayn Logo" 
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain"
          />
        </div>
      </div>

      {/* Bottom section - Call to action */}
      <div className="relative z-10 flex flex-col items-center space-y-3 sm:space-y-4 px-4">
        {/* Call to action text */}
        <p className="text-white text-center text-sm sm:text-base md:text-lg">
          Want to see how I can help your company hire better?
        </p>

        {/* Conditional button based on mode */}
        {isWebsiteMode ? (
          /* Keep Scrolling button for website mode */
          <button
            onClick={onKeepScrolling}
            className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-all duration-200 hover:scale-105 text-sm sm:text-base"
          >
            <Check className="w-4 h-4" />
            <span className="font-medium">Keep Scrolling</span>
            <ArrowDown className="w-4 h-4" />
          </button>
        ) : (
          /* Schedule a demo button for non-website mode */
          <button
            onClick={handleScheduleDemo}
            className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-all duration-200 hover:scale-105 text-sm sm:text-base"
          >
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Schedule a demo</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default EndCallScreen
